import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// One fetch per page load, shared by every component that needs it.
const cache = {};
const listeners = {};
const load = (key, url) => {
  if (!cache[key]) cache[key] = api.get(url).then((r) => r.data).catch(() => null);
  return cache[key];
};

/** Drop a shared value and re-fetch it for everyone showing it (old value stays until the new one lands). */
export const refreshShared = (key) => {
  delete cache[key];
  (listeners[key] || new Set()).forEach((bump) => bump());
};

const useShared = (key, url, refreshOnReturn = false) => {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    (listeners[key] = listeners[key] || new Set()).add(bump);
    const onReturn = () => document.visibilityState === "visible" && refreshShared(key);
    if (refreshOnReturn) document.addEventListener("visibilitychange", onReturn);
    return () => {
      listeners[key].delete(bump);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [key, refreshOnReturn]);
  useEffect(() => {
    let live = true;
    load(key, url).then((d) => { if (live && d !== null) setData(d); });
    return () => { live = false; };
  }, [key, url, tick]);
  return data;
};

/** { google: {rating, count, url} | null, service_media: { [serviceId]: [{kind, src, poster, caption}] } } */
export const usePublicSite = () => useShared("site", "/site/public");

/** { date: "YYYY-MM-DD" | null, window, is_today } — next open Mon–Fri estimate window.
 *  Re-checked whenever the visitor comes back to the tab and after the booking popup closes. */
export const useNextEstimate = () => useShared("next", "/availability/next", true);
export const refreshNextEstimate = () => refreshShared("next");

// "2 estimate slots left this week" — next week's open count once this week has none left.
export const slotsLeftText = (next) => {
  if (!next) return null;
  const slots = (k) => `${k} estimate slot${k === 1 ? "" : "s"}`;
  if (next.week_left > 0) return `${slots(next.week_left)} left this week`;
  if (next.next_week_open > 0) return `${slots(next.next_week_open)} open next week`;
  return null;
};

// "Today" only when the open window really is today; otherwise the weekday (plus the date when
// it's more than a week out) — never "today" on a weekend or closed day.
export const nextEstimateDay = (next) => {
  if (!next?.date) return null;
  if (next.is_today) return "Today";
  const d = new Date(`${next.date}T12:00:00`);
  const wd = d.toLocaleDateString("en-US", { weekday: "short" });
  const days = Math.round((d - new Date(new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" }) + "T12:00:00")) / 864e5);
  return days > 6 ? `${wd} ${d.getMonth() + 1}/${d.getDate()}` : wd;
};
